import { useMemo } from 'react';
import type { Item, SnapshotItem } from '../worklog/types';
import { scopeItemsToContract, ALL_EVENTS } from '../worklog/types';
import { fmtDuration } from '../worklog/format';
import { useUI } from '../state/UIContext';
import { useWorklog } from '../state/WorklogContext';
import { useStatus } from '../state/StatusContext';

function isStretchItem(it: Item): boolean {
  return /^s-/i.test(it.id || '');
}

function snapshotItemToItem(it: SnapshotItem): Item {
  return {
    id: it.id,
    label: it.label,
    status: it.status,
    block_reason: it.block_reason,
    estimate_minutes: it.estimate_minutes ?? null,
    priority: it.priority ?? null,
    actual_seconds: it.actual_seconds_derived,
    contract: it.contract,
    created_at: it.created_at,
    completed_at: it.completed_at ?? null,
    last_used_at: it.last_used_at ?? null,
  } as Item;
}

export function TasksTable() {
  const { state } = useWorklog();
  const { snapshot } = useStatus();
  const { timeColMode, setTimeColMode } = useUI();
  const items = useMemo(() => {
    const all = (snapshot?.items ?? []).map(snapshotItemToItem);
    return scopeItemsToContract(all, state.activeContract);
  }, [snapshot, state.activeContract]);

  // Per-item time scoped to the selected contract. Item ids repeat across
  // weeks for unrelated tasks, so the all-time actual_seconds is wrong here -
  // use the selected contract's item_seconds map from the snapshot.
  const itemSeconds = useMemo<Record<string, number> | undefined>(() => {
    const cs = snapshot?.contracts ?? [];
    const target = state.activeContract === ALL_EVENTS
      ? cs.find(c => c.name === 'all_events')
      : cs.find(c => c.name.toLowerCase() === state.activeContract.toLowerCase());
    return target?.item_seconds;
  }, [snapshot, state.activeContract]);

  const [mainItems, stretchItems] = useMemo(() => {
    const main: Item[] = [];
    const stretch: Item[] = [];
    for (const it of items) (isStretchItem(it) ? stretch : main).push(it);
    return [main, stretch];
  }, [items]);

  if (!items.length) {
    return (
      <>
        <h3>Tasks</h3>
        <div style={{ color: 'var(--text-mute)', fontStyle: 'italic', padding: '12px 0' }}>
          no items registered yet
        </div>
      </>
    );
  }

  const timeHeader = timeColMode === 'time' ? 'Time' : 'Actual / Est';

  const toggleTimeCol = () => setTimeColMode(timeColMode === 'time' ? 'both' : 'time');

  return (
    <>
      <h3>Tasks</h3>
      <table className="tasks-table">
        <thead>
          <tr>
            <th className="tt-prio">#</th>
            <th className="tt-id">ID</th>
            <th className="tt-label">Item</th>
            <th className="tt-status">Status</th>
            <th className="tt-time" onClick={toggleTimeCol} style={{ cursor: 'pointer' }}>
              {timeHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {mainItems.map(it => <ItemRow key={it.id} item={it} timeColMode={timeColMode} scopedSeconds={itemSeconds ? (itemSeconds[it.id] ?? 0) : undefined} />)}
        </tbody>
      </table>
      {stretchItems.length > 0 ? (
        <details className="stretch-details">
          <summary>Stretch items ({stretchItems.length})</summary>
          <table className="tasks-table">
            <thead>
              <tr>
                <th className="tt-prio">#</th>
                <th className="tt-id">ID</th>
                <th className="tt-label">Item</th>
                <th className="tt-status">Status</th>
                <th className="tt-time" onClick={toggleTimeCol} style={{ cursor: 'pointer' }}>
                  {timeHeader}
                </th>
              </tr>
            </thead>
            <tbody>
              {stretchItems.map(it => <ItemRow key={it.id} item={it} timeColMode={timeColMode} scopedSeconds={itemSeconds ? (itemSeconds[it.id] ?? 0) : undefined} />)}
            </tbody>
          </table>
        </details>
      ) : null}
    </>
  );
}

function ItemRow({
  item,
  timeColMode,
  scopedSeconds,
}: {
  item: Item;
  timeColMode: 'time' | 'both';
  scopedSeconds?: number;
}) {
  const idLabel = item.label || item.id;
  const status = (item.status as string) || 'open';
  const prio = typeof item.priority === 'number' && item.priority > 0 ? item.priority : '-';

  // Time worked on THIS item within the selected contract. scopedSeconds comes
  // from the snapshot contract's item_seconds map; it is the per-week figure.
  // Fall back to the all-time actual_seconds only if the snapshot predates the
  // per-contract map (scopedSeconds === undefined).
  const actualS = typeof scopedSeconds === 'number'
    ? scopedSeconds
    : (typeof item.actual_seconds === 'number' ? (item.actual_seconds as number) : 0);
  const estS = (item.estimate_minutes || 0) * 60;

  let timeCell = '-';
  if (timeColMode === 'time') {
    if (actualS > 0) timeCell = fmtDuration(actualS);
  } else if (actualS > 0 || estS > 0) {
    timeCell = (actualS > 0 ? fmtDuration(actualS) : '0m') + (estS > 0 ? ' / ' + fmtDuration(estS) : '');
  }

  return (
    <tr className={'tt-row tt-status-' + status}>
      <td className="tt-prio">{prio}</td>
      <td className="tt-id">{item.id}</td>
      <td className="tt-label">
        {idLabel}
        {item.block_reason ? (
          <span className="tt-block-reason" title={item.block_reason}> (blocked)</span>
        ) : null}
      </td>
      <td className="tt-status"><span className="tt-status-pill">{status}</span></td>
      <td className="tt-time">{timeCell}</td>
    </tr>
  );
}
